import { useState, useEffect, useMemo, useCallback } from 'react';
import { Group, Text, Box, UnstyledButton } from '@mantine/core';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import type { EventInput, EventClickArg, DatesSetArg } from '@fullcalendar/core';
import type { Session } from '../../../../types';
import type { SessionWithDetailsAndRsvp } from '../../../../services/api/sessions';
import type { GroupWithMembers } from '../../../../services/api/groups';
import {
  cohortEventsApi,
  type CohortEventWithDetails,
} from '../../../../services/api/cohortEvents';
import { getGroupAvatarColor } from '../../../../utils/groupColors';
import type { SessionTabValue } from './SessionsSubNav';
import classes from './SessionsCalendarView.module.css';

// ============================================================================
// Types
// ============================================================================

interface CalendarEvent extends EventInput {
  extendedProps: {
    eventType: 'session' | 'cohort_event';
    category: 'cohort_event' | 'group_session' | 'one_on_one' | 'workshop';
    session?: SessionWithDetailsAndRsvp;
    cohortEvent?: CohortEventWithDetails;
    groupId?: string;
    groupName?: string;
  };
}

interface SessionsCalendarViewProps {
  cohortId: string;
  sessions: SessionWithDetailsAndRsvp[];
  groups: GroupWithMembers[];
  activeTab: SessionTabValue;
  searchQuery: string;
  groupFilter: string[];
  coachFilter: string[];
  cohortEventsKey: number;
  onSessionClick: (session: Session) => void;
}

// ============================================================================
// Color Configuration
// ============================================================================

const CATEGORY_COLORS = {
  cohort_event: { border: '#2D9A8C', background: '#D4EDE7' },
  group_session: { border: '#4A90C2', background: '#E0F2FE' },
  one_on_one: { border: '#E07B4C', background: '#FFE3D6' },
  workshop: { border: '#8B5CB8', background: '#F3E8FF' },
} as const;

const LEGEND_ITEMS = [
  { key: 'cohort_event', label: 'Cohort Events', color: '#2D9A8C' },
  { key: 'group_session', label: 'Group Sessions', color: '#4A90C2' },
  { key: 'one_on_one', label: '1:1 Coaching', color: '#E07B4C' },
  { key: 'workshop', label: 'Workshops', color: '#8B5CB8' },
] as const;

// ============================================================================
// Helpers
// ============================================================================

function getSessionCategory(
  sessionType: string | null
): 'group_session' | 'one_on_one' | 'workshop' {
  if (sessionType === 'one_on_one_session' || sessionType === 'non_coaching_one_on_one') {
    return 'one_on_one';
  }
  if (sessionType === 'workshop_session') {
    return 'workshop';
  }
  return 'group_session';
}

function getSessionTitle(session: SessionWithDetailsAndRsvp): string {
  const category = getSessionCategory(session.session_type);
  if (category === 'one_on_one') {
    return `1:1: ${session.title || `Session ${session.session_number}`}`;
  }
  if (category === 'workshop') {
    return session.title || `Workshop ${session.session_number}`;
  }
  // Group session
  const groupName = session.group_name || 'Group';
  return `${groupName}: S${session.session_number}`;
}

/** Lighten a hex color for use as event background (for per-group colors) */
function lightenColor(hex: string, amount = 0.8): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`;
}

// ============================================================================
// Component
// ============================================================================

export function SessionsCalendarView({
  cohortId,
  sessions,
  groups,
  activeTab,
  searchQuery,
  groupFilter,
  coachFilter,
  cohortEventsKey,
  onSessionClick,
}: SessionsCalendarViewProps) {
  const [calendarView, setCalendarView] = useState<'dayGridMonth' | 'timeGridWeek'>(
    'dayGridMonth'
  );
  const [cohortEvents, setCohortEvents] = useState<CohortEventWithDetails[]>([]);

  // Fetch cohort events for calendar display
  useEffect(() => {
    cohortEventsApi
      .list(cohortId)
      .then(setCohortEvents)
      .catch(() => setCohortEvents([]));
  }, [cohortId, cohortEventsKey]);

  // Build group lookup for coach filtering
  const groupCoachMap = useMemo(() => {
    const map = new Map<string, string>();
    groups.forEach((g) => {
      if (g.coach_name) map.set(g.id, g.coach_name);
    });
    return map;
  }, [groups]);

  // Filter and map sessions to calendar events
  const calendarEvents = useMemo((): CalendarEvent[] => {
    const events: CalendarEvent[] = [];

    // --- Sessions ---
    const scheduledSessions = sessions.filter((s) => {
      // Must have a date/time
      if (!s.scheduled_start) return false;
      // Must belong to a group (exclude template sessions)
      if (!s.group_id) return false;
      // Exclude check-in sessions (AC34)
      if (s.session_type === 'non_coaching_one_on_one') return false;

      const category = getSessionCategory(s.session_type);

      // Category tab filter
      if (activeTab === 'groupCoaching' && category !== 'group_session') return false;
      if (activeTab === 'oneOnOne' && category !== 'one_on_one') return false;
      if (activeTab === 'workshop' && category !== 'workshop') return false;

      // Group filter
      if (groupFilter.length > 0 && !groupFilter.includes(s.group_id)) return false;

      // Coach filter
      if (coachFilter.length > 0) {
        const coachName = groupCoachMap.get(s.group_id);
        if (!coachName || !coachFilter.includes(coachName)) return false;
      }

      // Search filter
      if (searchQuery) {
        const title = getSessionTitle(s).toLowerCase();
        if (!title.includes(searchQuery.toLowerCase())) return false;
      }

      return true;
    });

    scheduledSessions.forEach((session) => {
      const category = getSessionCategory(session.session_type);
      let borderColor: string;
      let bgColor: string;

      if (activeTab === 'groupCoaching' && session.group_id && session.group_name) {
        // Per-group colors on Group Coaching tab
        const groupColor = getGroupAvatarColor(session.group_name, session.group_id);
        borderColor = groupColor;
        bgColor = lightenColor(groupColor, 0.75);
      } else {
        // Category colors
        const colors = CATEGORY_COLORS[category];
        borderColor = colors.border;
        bgColor = colors.background;
      }

      events.push({
        id: `session-${session.id}`,
        title: getSessionTitle(session),
        start: session.scheduled_start!,
        end: session.scheduled_end || undefined,
        borderColor,
        backgroundColor: bgColor,
        textColor: '#1A1A1A',
        extendedProps: {
          eventType: 'session',
          category,
          session,
          groupId: session.group_id || undefined,
          groupName: session.group_name || undefined,
        },
      });
    });

    // --- Cohort Events (only on "All" tab) ---
    if (activeTab === 'all') {
      const scheduledCohortEvents = cohortEvents.filter((e) => {
        if (!e.scheduled_start) return false;
        if (e.status === 'not_scheduled') return false;

        // Search filter
        if (searchQuery) {
          if (!e.event_name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        }

        return true;
      });

      scheduledCohortEvents.forEach((event) => {
        const colors = CATEGORY_COLORS.cohort_event;
        events.push({
          id: `event-${event.id}`,
          title: event.event_name,
          start: event.scheduled_start!,
          end: event.scheduled_end || undefined,
          borderColor: colors.border,
          backgroundColor: colors.background,
          textColor: '#1A1A1A',
          extendedProps: {
            eventType: 'cohort_event',
            category: 'cohort_event',
            cohortEvent: event,
          },
        });
      });
    }

    return events;
  }, [
    sessions,
    cohortEvents,
    activeTab,
    groupFilter,
    coachFilter,
    searchQuery,
    groupCoachMap,
  ]);

  // Show legend only on "All" tab
  const showLegend = activeTab === 'all';

  // Handle event click → open session detail drawer
  const handleEventClick = useCallback(
    (info: EventClickArg) => {
      const { extendedProps } = info.event;
      if (extendedProps.eventType === 'session' && extendedProps.session) {
        onSessionClick(extendedProps.session as Session);
      }
      // Cohort events don't open a drawer (no equivalent slide-out exists)
    },
    [onSessionClick]
  );

  // Track view changes from FullCalendar's internal buttons
  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    if (arg.view.type === 'dayGridMonth') setCalendarView('dayGridMonth');
    else if (arg.view.type === 'timeGridWeek') setCalendarView('timeGridWeek');
  }, []);

  return (
    <Box>
      {/* Legend */}
      {showLegend && (
        <Group gap="lg" mb="sm" mt="xs">
          {LEGEND_ITEMS.map((item) => (
            <Group key={item.key} gap={6}>
              <Box
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                }}
              />
              <Text size="sm" c="dimmed">
                {item.label}
              </Text>
            </Group>
          ))}
        </Group>
      )}

      {/* Calendar */}
      <Box
        className={classes.calendarWrapper}
        style={{
          '--fc-border-color': '#E8E6E1',
          '--fc-today-bg-color': 'rgba(45, 71, 57, 0.04)',
          '--fc-page-bg-color': '#FFFFFF',
          '--fc-neutral-bg-color': '#F5F3EF',
          '--fc-event-border-color': 'transparent',
        } as React.CSSProperties}
      >
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin]}
          initialView={calendarView}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'timeGridWeek,dayGridMonth',
          }}
          buttonText={{
            today: 'Today',
            month: 'Month',
            week: 'Week',
          }}
          events={calendarEvents}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          height="auto"
          dayMaxEvents={3}
          moreLinkClick="popover"
          eventDisplay="block"
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short',
          }}
          slotMinTime="07:00:00"
          slotMaxTime="20:00:00"
          scrollTime="08:00:00"
          allDaySlot={true}
          allDayText="All Day"
          nowIndicator={true}
          weekends={true}
          fixedWeekCount={false}
          eventContent={(arg) => {
            const { event } = arg;
            const borderColor = event.borderColor || '#4A90C2';
            const bgColor = event.backgroundColor || '#E0F2FE';
            const isWeekView = arg.view.type === 'timeGridWeek';

            return (
              <UnstyledButton
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  padding: isWeekView ? '4px 6px' : '2px 6px',
                  backgroundColor: bgColor,
                  borderLeft: `3px solid ${borderColor}`,
                  borderRadius: 4,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s ease',
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Text
                  size="xs"
                  fw={500}
                  c="#1A1A1A"
                  lineClamp={isWeekView ? 3 : 1}
                  style={{ lineHeight: 1.3 }}
                >
                  {event.title}
                </Text>
                {isWeekView && arg.timeText && (
                  <Text size="xs" c="#666666" mt={2} style={{ lineHeight: 1.2 }}>
                    {arg.timeText}
                  </Text>
                )}
              </UnstyledButton>
            );
          }}
        />
      </Box>
    </Box>
  );
}

export default SessionsCalendarView;
